import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import {
  DocumentThumbnail,
  MiDetailFields,
  MiFileUpload,
  MiModal,
} from "@miplatform/components";
import {
  BUTTON_COLORS,
  BUTTON_VARIANTS,
  INPUT_TYPES,
} from "@miplatform/constants";
import { createUUID } from "@miplatform/utils";
import { zodResolver } from "@hookform/resolvers/zod";
import { mdiPencilOutline, mdiPlus } from "@mdi/js";
import { Box, Typography, useTheme } from "@mui/material";
import { MATERIAL_SOURCE_VALUES } from "./materialConstants";

const getDefaultValues = (material) => ({
  materialId: material?.materialId ?? createUUID(),
  issueId: material?.issueId ?? "",
  description: material?.description ?? "",
  brand: material?.brand ?? "",
  quantity: Number(material?.quantity ?? 1),
  source: material?.source ?? "",
  requiresReplacement: String(material?.requiresReplacement ?? false),
});

const materialSchema = z.object({
  materialId: z.string().uuid(),
  issueId: z.string().uuid("Task is required."),
  description: z.string().trim().min(1, "Material is required."),
  brand: z.string().trim().min(1, "Brand is required."),
  quantity: z
    .number({ invalid_type_error: "Quantity is required." })
    .int("Quantity must be a whole number.")
    .min(1, "Quantity must be at least 1."),
  source: z.enum(Object.values(MATERIAL_SOURCE_VALUES), {
    errorMap: () => ({ message: "Source is required." }),
  }),
  requiresReplacement: z.enum(["true", "false"]),
});

export const MaterialModal = ({
  open,
  issues = [],
  material = null,
  isLoading = false,
  closeModal,
  onSaveMaterial,
}) => {
  const theme = useTheme();
  const isEditing = Boolean(material);
  const [file, setFile] = useState(null);
  const [existingDocument, setExistingDocument] = useState(null);
  const [documentToDelete, setDocumentToDelete] = useState(null);
  const {
    control,
    formState: { isValid },
    handleSubmit,
    reset,
    setValue,
    watch,
  } = useForm({
    mode: "onChange",
    defaultValues: getDefaultValues(material),
    resolver: zodResolver(materialSchema),
  });
  const quantity = watch("quantity");

  const taskOptions = useMemo(
    () =>
      issues.map((issue, index) => ({
        label: `${index + 1}. ${
          issue.issueResult?.title ||
          issue.title ||
          issue.originalDescription ||
          "Untitled Task"
        }`,
        value: issue.id,
        disabled: !issue.issueResult?.id,
      })),
    [issues]
  );

  useEffect(() => {
    reset(getDefaultValues(material));
    setFile(null);
    setExistingDocument(material?.documents?.[0] ?? null);
    setDocumentToDelete(null);
  }, [material, open, reset]);

  const handleClose = () => {
    reset(getDefaultValues(material));
    setFile(null);
    setExistingDocument(material?.documents?.[0] ?? null);
    setDocumentToDelete(null);
    closeModal();
  };

  const handleDeleteExistingDocument = () => {
    setDocumentToDelete(existingDocument);
    setExistingDocument(null);
  };

  const handleSaveMaterial = (data) =>
    onSaveMaterial({ ...data, file, documentToDelete });

  const fields = [
    {
      label: "Task",
      name: "issueId",
      required: true,
      fieldType: INPUT_TYPES.SELECT,
      options: taskOptions,
      disabled: isEditing,
      width: "100%",
    },
    {
      label: "Material",
      name: "description",
      required: true,
      width: "100%",
    },
    {
      label: "Brand",
      name: "brand",
      required: true,
      width: "100%",
    },
    {
      label: "Quantity",
      name: "quantity",
      required: true,
      fieldType: INPUT_TYPES.NUMERIC,
      value: quantity,
      decimalScale: 0,
      allowNegative: false,
      allowLeadingZeros: false,
      setValue: (value) =>
        setValue("quantity", value ?? null, {
          shouldDirty: true,
          shouldValidate: true,
        }),
      width: "50%",
    },
    {
      label: "Source",
      name: "source",
      required: true,
      fieldType: INPUT_TYPES.SELECT,
      options: Object.values(MATERIAL_SOURCE_VALUES).map((value) => ({
        label: value,
        value,
      })),
      width: "50%",
    },
    {
      label: "Requires Replacement?",
      name: "requiresReplacement",
      fieldType: INPUT_TYPES.RADIO_GROUP,
      options: [
        { label: "Yes", value: "true" },
        { label: "No", value: "false" },
      ],
      width: "100%",
    },
  ];

  return (
    <MiModal
      open={open}
      closeModal={handleClose}
      loading={isLoading}
      title={isEditing ? "Edit Material" : "Add Material"}
      description={
        isEditing
          ? "Update the material details."
          : "Select a task and enter the material details."
      }
      titleIcon={isEditing ? mdiPencilOutline : mdiPlus}
      leftIconColor={theme.palette.primary.main}
      width={448}
      actions={[
        {
          name: "Cancel",
          variant: BUTTON_VARIANTS.TEXT,
          color: BUTTON_COLORS.BLACK,
          onClick: handleClose,
        },
        {
          name: isEditing ? "Save Material" : "Add Material",
          variant: BUTTON_VARIANTS.CONTAINED,
          disabled: isLoading || !isValid || file?.error,
          onClick: handleSubmit(handleSaveMaterial),
        },
      ]}
    >
      <MiDetailFields detailFields={fields} control={control} />
      <Box px={1} pt={1}>
        {existingDocument ? (
          <Box display="flex" flexDirection="column" gap={1}>
            <Typography variant="body2">Attached File</Typography>
            <Box display="flex" alignItems="center" gap={1.5}>
              <DocumentThumbnail
                document={existingDocument}
                documentCount={1}
                dimension={64}
                onDelete={isLoading ? undefined : handleDeleteExistingDocument}
              />
              <Typography variant="body2">
                {existingDocument.title ||
                  existingDocument.metadata?.title ||
                  existingDocument.filename ||
                  "Attached file"}
              </Typography>
            </Box>
          </Box>
        ) : (
          <MiFileUpload
            dragAndDrop
            showUploadButton
            multiple={false}
            disabled={isLoading}
            onChange={(files) => setFile(files?.[0] ?? null)}
            onDelete={() => setFile(null)}
            title="Upload File"
          />
        )}
      </Box>
    </MiModal>
  );
};
